import type { Zone } from '@seek/audience-zones';
import type { CompatibleLanguage } from '@seek/ca-myprofile-utils';
import type { Brand, Country, SiteName } from '@seek/melways-sites';
import { adapter } from '@seek/tealium-adapter';

interface State {
  currentPage: string;
  isLoggedIn?: boolean;
  loginId?: number;
  profileGuid?: string;
  error?: string;
  currentPageSection?: string;
  brand?: string;
  siteCountry?: string;
  zone?: Zone | string;
  siteLanguage?: string;
  site?: SiteName | string;
}

interface TrackLinkProps {
  eventName: string;
  currentPageSection?: string;
  link?: string;
  linkType?: string;
  isExitLink?: boolean;
  siteCountry?: string;
  siteLanguage?: string;
}
interface TrackPageProps {
  pageName: string;
  eventName: string;
  siteCountry?: string;
  siteLanguage?: string;
}

type SetLoggedInState = (obj: {
  isLoggedIn: boolean;
  loginId?: number;
  profileGuid?: string;
  error?: string;
}) => State;

export interface MiddlewareAnalytics {
  state: State;
  setLoggedInState: SetLoggedInState;
  trackPageView: any;
  trackLink: any;
  error?: string;
}

const mockPageAnalytics = {
  siteCountry: '',
  brand: '',
  siteLanguage: '',
  zone: '',
};

interface AnalyticsProps {
  pageAnalytics?: {
    siteCountry: Country;
    brand: Brand;
    siteLanguage: CompatibleLanguage;
    zone: Zone;
  };
  analyticsService?: any;
}

export default (analyticsProps: AnalyticsProps): MiddlewareAnalytics => {
  const { pageAnalytics = mockPageAnalytics, analyticsService = adapter({}) } =
    analyticsProps;
  const { brand, siteCountry, siteLanguage, zone } = pageAnalytics;

  let state: State = {
    currentPage: 'account settings',
    brand,
    siteCountry,
    zone,
    siteLanguage,
  };

  const setLoggedInState: SetLoggedInState = ({
    isLoggedIn,
    loginId,
    profileGuid,
    error,
  }) => {
    state = {
      ...state,
      isLoggedIn,
      loginId,
      profileGuid,
      error,
    };
    return state;
  };

  const trackPageView = (props: TrackPageProps) => {
    const { eventName, ...restProps } = props;
    analyticsService.view(eventName, {
      ...state,
      ...restProps,
      siteCountry: state.siteCountry || props.siteCountry,
      siteLanguage: state.siteLanguage || props.siteLanguage,
    });
  };

  const trackLink = (props: TrackLinkProps) => {
    const { eventName, ...restProps } = props;
    analyticsService.link(eventName, {
      ...state,
      ...restProps,
      siteCountry: state.siteCountry || props.siteCountry,
      siteLanguage: state.siteLanguage || props.siteLanguage,
    });
  };

  return {
    state,
    setLoggedInState,
    trackPageView,
    trackLink,
  };
};
